import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type AppRoute =
  | '/'
  | '/instrucoes'
  | '/diagnostico'
  | '/identificacao'
  | '/consentimento'
  | '/processando'
  | '/resultado'
  | '/admin/login'
  | '/admin/dashboard'
  | '/admin/leads'
  | '/componentes'
  | '/erro';

interface RouterContextType {
  currentPath: string;
  navigate: (path: string) => void;
  params: Record<string, string>;
}

const RouterContext = createContext<RouterContextType>({
  currentPath: '/',
  navigate: () => {},
  params: {},
});

export function RouterProvider({ children }: { children: ReactNode }) {
  const [currentPath, setCurrentPath] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return window.location.pathname || '/';
    }
    return '/';
  });

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string) => {
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', path);
      setCurrentPath(path.split('?')[0] || '/');
      window.scrollTo(0, 0);
    }
  };

  // Extrai query params
  const getParams = (): Record<string, string> => {
    if (typeof window === 'undefined') return {};
    const searchParams = new URLSearchParams(window.location.search);
    const params: Record<string, string> = {};
    searchParams.forEach((val, key) => {
      params[key] = val;
    });
    return params;
  };

  return (
    <RouterContext.Provider value={{ currentPath, navigate, params: getParams() }}>
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter() {
  return useContext(RouterContext);
}
